import { UtilsService } from '../../services/utils.service';
import { LocationType } from '../../types/location.type';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  EventResponseCategory,
  EventResponseEvent,
} from '../../types/event-response.type';
import { NavigationService } from '../../services/navigation.service';
import { UntypedFormControl } from '@angular/forms';
import { EventsHttpService } from '../../services/events.http.service';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { DEFAULT_BANNER_IMAGE } from '../../constants/constants';
import { ActivatedRoute } from '@angular/router';
import { unwrapCategoriesUtil } from '../../utils/unwrapCategories.util';
import { AuthService } from '../../services/auth.service';
import { PopupService } from '../../services/popup.service';
import { TranslateService } from '../../services/translate.service';

@Component({
  selector: 'app-event',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input() event: EventResponseEvent;
  @Input() minified = true;
  @Input() inline = false;
  @Input() backgroundFullWidth: boolean = false;
  @Input() background = true;
  @Input() showViewButton = true;
  @Input() isActive: boolean = false;
  @Input() hasClickEvent: boolean = false;

  @ViewChild('categoryList') categoryList: ElementRef<HTMLDivElement>;
  @ViewChild('eventRef') eventRef: ElementRef<HTMLDivElement> | undefined;
  @ViewChild('unauthorizedUserPopup', { static: true })
  unauthorizedUserPopup: ElementRef;

  @Output() eventClicked = new EventEmitter();

  @HostListener('mouseleave')
  public onMouseleave(): void {
    this.el.nativeElement.classList.remove('event--hover');
  }

  @HostListener('mouseover', ['$event'])
  public onMouseover(event: MouseEvent): void {
    if (!this.hasClickEvent) {
      return;
    }

    const titleEl = (event.target as HTMLElement)?.closest('[title]');

    if (!titleEl || (titleEl && titleEl.isEqualNode(this.el.nativeElement))) {
      this.el.nativeElement.classList.add('event--hover');
    } else {
      this.el.nativeElement.classList.remove('event--hover');
    }
  }

  private onResizeObserver = new ResizeObserver(() =>
    this.displayCategoriesThatFit()
  );

  @Output() goToEventPageClicked: EventEmitter<EventResponseEvent> =
    new EventEmitter<EventResponseEvent>();

  public likeControl = new UntypedFormControl(false);
  public displayedCategories: EventResponseCategory[] = [];
  public hiddenCategoryCount: number = 0;
  public hideListItems: boolean = false;

  get eventBannerImage(): string {
    if (this.event.banner_image) {
      return this.event.banner_image;
    }

    if (this.event.categories.length === 1) {
      if (this.event.categories[0]?.banner_image) {
        return this.event.categories[0].banner_image;
      }

      const categories = unwrapCategoriesUtil(
        this.activatedRoute.snapshot.data['categories'] ?? []
      );

      const categoryBanner = categories.find(
        (cat) => cat.category_id === this.event.categories[0].category_id
      )?.banner_image;

      if (categoryBanner) {
        return categoryBanner;
      }
    }

    return DEFAULT_BANNER_IMAGE;
  }

  constructor(
    private navigation: NavigationService,
    private eventService: EventsHttpService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private popupService: PopupService
  ) {}

  public location: LocationType | undefined;
  public isExpanded: boolean = false;

  get hiddenCategoryTitle(): string {
    return this.hiddenCategories.map((cat) => cat.category_title).join(', ');
  }

  get hiddenCategories(): EventResponseCategory[] {
    return this.event.categories.filter(
      (item) =>
        !this.displayedCategories.find(
          (cat) => cat.category_id === item.category_id
        )
    );
  }

  getTitle(categoryName: string): string {
    return `${TranslateService.translate(
      'view'
    )} ${categoryName} ${TranslateService.translate('event_badge_events')}`;
  }

  ngOnInit(): void {
    // todo: implement proper event button handling
    this.likeControl.valueChanges.subscribe((val) => {
      if (this.authService.user) {
        this.toggleLike(val).subscribe();
      } else {
        this.openUnauthorizedUserPopup();
        this.likeControl.setValue(false, {
          emitEvent: false,
        });
      }
    });
    this.eventService.likeChanges$
      .pipe(
        filter((change) => change && change.event_id === this.event.event_id)
      )
      .subscribe((change) => {
        this.likeControl.setValue(change.value, { emitEvent: false });
      });
  }

  ngOnChanges({ event }: SimpleChanges): void {
    if (event) {
      this.likeControl.setValue(this.event.user_has_liked, {
        emitEvent: false,
      });
      this.location = UtilsService.parseLocation(this.event.event_location);

      this.isExpanded = false;
      this.restartResizeObserver();
      this.displayCategoriesThatFit();
    }
  }

  ngAfterViewInit(): void {
    this.restartResizeObserver();
  }

  restartResizeObserver(): void {
    if (this.eventRef) {
      this.onResizeObserver.unobserve(this.eventRef.nativeElement);
      this.onResizeObserver.observe(this.eventRef.nativeElement);
    }
  }

  displayCategoriesThatFit(): void {
    if (!this.categoryList) {
      return;
    }

    this.hideListItems = true;
    this.displayedCategories = this.event.categories;
    const displayedCategories: number[] = [];
    this.cdr.detectChanges();

    const container = this.categoryList.nativeElement;
    const containerWidth = container.clientWidth;
    let displayedElementWidth = 50; // 50 is the estimated with that the `+ X` will take

    (container.childNodes as NodeListOf<HTMLDivElement>).forEach(
      (elem: HTMLDivElement) => {
        const categoryId = parseInt(elem?.dataset?.['id'] ?? '');

        if (categoryId && !displayedCategories.includes(categoryId)) {
          displayedElementWidth += elem.clientWidth;

          if (displayedElementWidth < containerWidth) {
            displayedCategories.push(categoryId);
          }
        }
      }
    );

    this.hiddenCategoryCount =
      this.event.categories.length - displayedCategories.length;
    this.displayedCategories = this.event.categories.filter((cat) =>
      displayedCategories.includes(cat.category_id)
    );
    this.hideListItems = false;
    this.cdr.detectChanges();
  }

  openRegistration(event: Event): void {
    event.stopPropagation();
    this.navigation.goToEventRegistration(this.event.event_id);
  }

  goToEventPage(event: Event, id: number): void {
    event.stopPropagation();
    this.navigation.goToEventPage(id);
    this.goToEventPageClicked.emit(this.event);
  }

  openUnauthorizedUserPopup(): void {
    this.popupService.showPopup(this.unauthorizedUserPopup);
  }

  toggleLike(value: boolean): Observable<Object> {
    return this.eventService.toggleEventLike(this.event.event_id, value);
  }

  ngOnDestroy(): void {
    if (this.eventRef) {
      this.onResizeObserver.unobserve(this.eventRef.nativeElement);
    }
  }

  get dynamicStyles(): Record<string, string> {
    return {
      'background-image': `url(${this.eventBannerImage})`,
    };
  }

  handleEventClick(): void {
    if (this.el.nativeElement.classList.contains('event--hover')) {
      this.eventClicked.emit();
    }
  }
}
